import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import { Button, Skeleton } from "@/components/ui";
import { httpRequest } from "@/lib";
import {
  COST_UNAVAILABLE,
  DAILY_BRIEFING_ROUTE,
  type DailyBriefing,
  decodeDailyBriefingResponse,
  formatMoney,
  formatSeconds,
  todayInputValue,
} from "@/pages/reports/daily-briefing-api";

type Props = {
  /** False for a role the API answers 403 to; the card is not rendered. */
  canRead: boolean;
};

/**
 * W2-10 (SCRUM-184): the four headline numbers of today, on the dashboard,
 * linking to the full briefing. Compact on purpose — the page is where the
 * detail lives.
 */
export function DailyBriefingCard({ canRead }: Props) {
  const [briefing, setBriefing] = useState<DailyBriefing | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const response = await httpRequest.get("/session/reports/daily-briefing", {
        params: { date: todayInputValue() },
      });
      setBriefing(decodeDailyBriefingResponse(response.data).data);
    } catch {
      setBriefing(null);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    void load();
  }, [canRead, load]);

  if (!canRead) return null;

  return (
    <section className="rounded-xl border border-app-border bg-app-surface p-5 shadow-premium-xs">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-app-fg-strong">
          Briefing de hoje
        </h2>
        <Link
          to={DAILY_BRIEFING_ROUTE}
          className="inline-flex items-center gap-1 text-sm font-medium text-brand underline-offset-4 hover:underline"
        >
          Ver briefing completo
          <ArrowRight className="size-4" aria-hidden="true" />
        </Link>
      </header>

      {loading ? (
        <div
          role="status"
          aria-label="Carregando o briefing de hoje"
          className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4"
        >
          {[0, 1, 2, 3].map((slot) => (
            <Skeleton key={slot} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : null}

      {!loading && failed ? (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <p className="text-sm text-app-fg-muted">
            Não foi possível carregar o briefing de hoje.
          </p>
          <Button variant="outline" size="sm" onClick={() => void load()}>
            Tentar novamente
          </Button>
        </div>
      ) : null}

      {!loading && !failed && briefing ? (
        <>
        <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div>
            <dt className="text-xs text-app-fg-muted">Conversas</dt>
            <dd className="font-display text-2xl font-medium text-app-fg-strong">
              {briefing.conversations.total}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-app-fg-muted">Primeira resposta</dt>
            <dd className="font-display text-2xl font-medium text-app-fg-strong">
              {formatSeconds(briefing.first_response_seconds.p50)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-app-fg-muted">Agendamentos pela IA</dt>
            <dd className="font-display text-2xl font-medium text-app-fg-strong">
              {briefing.bookings.by_ai}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-app-fg-muted">Aprovações pendentes</dt>
            <dd className="font-display text-2xl font-medium text-app-fg-strong">
              {briefing.hitl.pending}
            </dd>
          </div>
        </dl>

        {/*
          SCRUM-194: the day's AI cost, in reais. It is a line and not a fifth
          headline number because it is a bill, not a result — and when the two
          runtimes cannot both be read it says so instead of drawing a zero.
        */}
        <p className="mt-4 text-xs text-app-fg-muted">
          Custo de IA:{" "}
          <span className="font-semibold text-app-fg-strong">
            {briefing.llm_cost
              ? formatMoney(briefing.llm_cost.brl, "BRL")
              : COST_UNAVAILABLE}
          </span>
        </p>
        </>
      ) : null}
    </section>
  );
}
